import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, Target, Award, Users } from "lucide-react";
import { Link } from "react-router-dom";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";

const stats = [
  { value: "100%", label: "Eco-Friendly", icon: Leaf, color: "green" as const },
  { value: "2030", label: "Vision Aligned", icon: Target, color: "gold" as const },
  { value: "Premium", label: "Quality", icon: Award, color: "blue" as const },
  { value: "B2B/B2G", label: "Partners", icon: Users, color: "purple" as const },
];

const colorStyles = {
  green: {
    bg: "bg-gradient-to-br from-eco-green/15 to-eco-green/5",
    iconBg: "bg-gradient-to-br from-eco-green to-eco-green-light",
    text: "text-eco-green",
    border: "border-eco-green/20 hover:border-eco-green/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--eco-green)/0.3)]",
  },
  gold: {
    bg: "bg-gradient-to-br from-primary/15 to-amber-glow/5",
    iconBg: "bg-gradient-to-br from-primary to-amber-glow",
    text: "text-primary",
    border: "border-primary/20 hover:border-primary/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.3)]",
  },
  blue: {
    bg: "bg-gradient-to-br from-sky-blue/15 to-sky-blue/5",
    iconBg: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
    text: "text-sky-blue",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--sky-blue)/0.3)]",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple/15 to-purple/5",
    iconBg: "bg-gradient-to-br from-purple to-rose",
    text: "text-purple",
    border: "border-purple/20 hover:border-purple/40",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--purple)/0.3)]",
  },
};

const About = () => {
  return (
    <section id="about" className="py-24 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
      <div className="absolute -right-40 top-1/3 w-80 h-80 rounded-full bg-primary/10 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute left-1/3 bottom-0 w-96 h-96 rounded-full bg-sky-blue/5 blur-3xl" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/30' : 'bg-sky-blue/30'}`}
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <VibrantSectionHeader
              badge="About Us"
              title="Sustainable Development"
              badgeColor="green"
            />
            <div className="text-left -mt-8">
              <p className="text-muted-foreground text-lg mb-6">
                We chose sustainability because we operate in an eco-conscious manner and believe that every strategy, product, and service can leave a green footprint.
              </p>
              <p className="text-muted-foreground text-lg mb-8">
                Our sustainable mobility solutions and technologies are aligned with the broader goals that drive our community forward, in step with our Kingdom's Vision 2030.
              </p>
              <Button 
                size="xl" 
                asChild
                className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-105"
              >
                <Link to="/about">
                  Learn More About Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => {
              const style = colorStyles[stat.color];
              return (
                <div
                  key={stat.label}
                  className={`group p-8 rounded-2xl ${style.bg} border ${style.border} backdrop-blur-sm text-center transition-all duration-500 hover:-translate-y-2 ${style.glow}`}
                >
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-xl ${style.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className={`text-3xl md:text-4xl font-bold ${style.text} mb-2`}>
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
